import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useParams, useHistory } from 'react-router'
import Button, { ButtonVariant } from 'design-system/button/src/Button'
import LessonCard from 'components/courses/lessons/components/LessonCard'
import CourseSidebard from 'components/ui/course-sidebar/CourseSidebar'
import Loader from 'components/ui/loader/Loader'
import * as fromLessonActions from 'store/actions/lessons.action'
import * as fromActions from 'store/actions/courses.action'
import { IMainState } from 'store/states'
import { ILessonModel } from 'models/lesson.model'
import { IAssignedLessonModel } from 'models/assignedLesson.model'
import { CourseProvider, useAccessToPreview } from 'contexts'
import { paths } from 'routes/paths'
import { AssignedLessonStateEnum } from 'enum'
import { orderLessons } from 'helpers/courses/order'
import Wrapper from 'components/reusables/wrapper'
import { IEvaluationResume } from 'models/evaluation.model'
import { Container, Buttons } from './styled'

enum nextLessonButtonLabel {
  next = 'next',
  finish = 'finish',
}

const Index = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const accessPreview = useAccessToPreview()
  const loading = false
  const urlParams = useParams()
  // @ts-ignore
  const courseId = urlParams.courseId

  const [nextButtonLabel] = useState(nextLessonButtonLabel.next)
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0)
  const [courseProgress, setCourseProgress] = useState(
    [] as IAssignedLessonModel[]
  )
  const [onEvaluation, setOnEvaluation] = useState(false)
  const { course } = useSelector(({ courses }: IMainState) => courses)

  const [evaluation, setEvaluation] = useState<IEvaluationResume | null>(null)
  const lessons = useMemo(() => orderLessons(course.lessons || []), [course])
  const currentLesson: ILessonModel | null = lessons[currentLessonIndex]
  const hasPrevLesson = currentLessonIndex > 0
  const hasNextLesson = currentLessonIndex < lessons.length - 1

  useEffect(() => {
    dispatch({ type: fromActions.GET_COURSE, payload: courseId })
  }, [courseId, dispatch])

  useEffect(() => {
    if (lessons[0] && lessons[0].id) {
      const lessonProgressFakeAux = [] as IAssignedLessonModel[]
      lessonProgressFakeAux.push({
        id: lessons[0].id,
        state: AssignedLessonStateEnum.InProgress,
        enrolId: 0,
        lessonId: lessons[0].id,
      })
      setCourseProgress(lessonProgressFakeAux)
      setCurrentLessonIndex(0)
    }
  }, [lessons, dispatch])

  const handleGoToLesson = (lesson: ILessonModel, lessonIndex: number) => {
    setCurrentLessonIndex(lessonIndex)
    lesson.id !== currentLesson.id &&
      dispatch({
        type: fromLessonActions.LESSONS_GET_LESSON,
        payload: lesson.id,
      })
  }

  const handleGetPrevLesson = () => {
    const nextLessonIndex = currentLessonIndex - 1

    if (hasPrevLesson) {
      const prevLesson = lessons[nextLessonIndex]

      if (prevLesson) {
        dispatch({
          type: fromLessonActions.LESSONS_GET_LESSON,
          payload: prevLesson.id,
        })
        setCurrentLessonIndex(nextLessonIndex)
        setOnEvaluation(false)
      }
    }
  }

  const handleGetNextLesson = () => {
    const nextLessonIndex = currentLessonIndex + 1

    if (hasNextLesson) {
      const nextLesson = lessons[nextLessonIndex]

      if (nextLesson) {
        dispatch({
          type: fromLessonActions.LESSONS_GET_LESSON,
          payload: nextLesson.id,
        })
        setCurrentLessonIndex(nextLessonIndex)
        setOnEvaluation(false)
      }
    }
  }

  useEffect(() => {
    if (accessPreview.loading) return
    if (!accessPreview.isAllowedToPreview) history.push(paths.home.root)
  }, [accessPreview, history])

  return loading ? (
    <Loader overlay color />
  ) : (
    <Container>
      <CourseSidebard
        newMessages={0}
        course={course}
        chatActive={false}
        hideResume={true}
        courseProgress={courseProgress}
        currentLesson={currentLesson}
        handleGoToLesson={handleGoToLesson}
        lessons={lessons}
        onEvaluation={onEvaluation}
        setOnEvaluation={setOnEvaluation}
        evaluation={evaluation}
      />
      <Wrapper shadow={!onEvaluation}>
        {(currentLesson?.id || 0) > 0 && (
          <LessonCard
            currentLesson={currentLesson}
            canEdit={false}
            onEvaluation={onEvaluation}
            setOnEvaluation={setOnEvaluation}
            evaluation={evaluation}
            setEvaluation={setEvaluation}
          />
        )}
        <Buttons>
          {hasPrevLesson ? (
            <Button
              variant={ButtonVariant.Secondary}
              onClick={handleGetPrevLesson}
            >
              {t('commons.buttons.previous')}
            </Button>
          ) : (
            <div />
          )}
          {hasNextLesson && (
            <Button onClick={handleGetNextLesson}>
              {t(`commons.buttons.${nextButtonLabel}`)}
            </Button>
          )}
        </Buttons>
      </Wrapper>
    </Container>
  )
}

const Preview = () => (
  <CourseProvider previewMode>
    <Index />
  </CourseProvider>
)

export default Preview
